import ReactEcharts from "echarts-for-react";
import { hexColor } from "../../../../../enums/colors";
import { TbCircleFilled } from "react-icons/tb";
import { t } from "i18next";
import { DataRow, SummaryPersonUsageRow, SummaryUsageRow } from "../../../../../stores/TabsStore";
import { useAppStore } from "../../../../../stores/RootStore";
import { Equipment } from "../../../../../types/types";
import _ from "lodash";
import { Duration } from "luxon";

export const EquipmentLoaded = ({equipment, data}: {equipment: Equipment, data: DataRow[]}) => {
  const {theme} = useAppStore()
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.turned_off]

  const getOptions = () => ({
    series: [
      {
        name: `${equipment.attributes.name}`,
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: _.sumBy(data, "availability.worked.seconds"), color: hexColor.worked },
          { name: "idle", value: _.sumBy(data, "availability.stopped.seconds"), color: hexColor.stopped },
          { name: "turned_off", value: _.sumBy(data, "availability.off.seconds"), color: hexColor.turned_off},
        ],
      },
    ]
  });


  return (
    <div className="flex flex-col w-full text-sm my-4 px-2">
      <div className="grid grid-cols-1 lg:grid-cols-3 my-4 gap-4">
        <div className="flex w-full justify-center lg:px-6">
          <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
        </div>
        <div className="flex flex-col w-full gap-3 lg:border-l border-border lg:px-6">
            <div className="grid grid-cols-2">
              <div className="flex font-semibold">{t('availability')}</div>
              <div className="flex font-semibold">{Duration.fromObject({seconds: _.sumBy(data, "availability.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "availability.total.seconds") / (_.sumBy(data, "availability.total.seconds") + _.sumBy(data, "unavailability.total.seconds"))) * 100).toFixed(2)} %)</div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
                  <span className="text-secondary-foreground">{t('worked')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "availability.worked.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "availability.worked.percent") / data.length) * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('turned_off')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "availability.off.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "availability.off.percent") / data.length) * 100).toFixed(2)} %)</span>
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
                  <span className="text-secondary-foreground">{t('stopped')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "availability.stopped.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "availability.stopped.percent") / data.length) * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.maintenance}}/>
                  <span className="text-secondary-foreground">{t('maintenance')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "maintenance.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "maintenance.total.percent") / data.length) * 100).toFixed(2)} %)</span>
              </div>
            </div>
        </div>
        <div className="flex flex-col w-full gap-3 lg:border-l border-border lg:px-6">
          <div className="grid grid-cols-2">
            <div className="flex font-semibold">{t('unavailability')}</div>
            <div className="flex font-semibold">{Duration.fromObject({seconds: _.sumBy(data, "unavailability.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "unavailability.total.seconds") / (_.sumBy(data, "availability.total.seconds") + _.sumBy(data, "unavailability.total.seconds"))) * 100).toFixed(2)} %)</div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <TbCircleFilled className="w-3 h-3" style={{color: hexColor.broken}}/>
                <span className="text-secondary-foreground">{t('broken')}:</span>
              </div>
              <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "broken.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "broken.total.percent") / data.length) * 100).toFixed(2)} %)</span>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <TbCircleFilled className="w-3 h-3" style={{color: hexColor.repair}}/>
                <span className="text-secondary-foreground">{t('repair')}:</span>
              </div>
              <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "repair.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "repair.total.percent") / data.length) * 100).toFixed(2)} %)</span>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <TbCircleFilled className="w-3 h-3" style={{color: hexColor.service}}/>
                <span className="text-secondary-foreground">{t('service')}:</span>
              </div>
              <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "service.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "service.total.percent") / data.length) * 100).toFixed(2)} %)</span>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                <span className="text-secondary-foreground">{t('turned_off')}:</span>
              </div>
              <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "unavailability.off.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "unavailability.off.percent") / data.length) * 100).toFixed(2)} %)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}